/**
 * "Base covers · {site}" — the homepage's fast entry lens on the occupancy store
 * (POS2-1259 CP5).
 *
 * Replaces `covers-dialog.tsx`. The shape a user sees is unchanged — one figure per Mon–Sun
 * week — but **weeks are no longer what gets stored**. On save the figure is split across the
 * seven days by the site's weekday pattern shape and written as daily overrides flagged
 * `source='widget'`, which is exactly what decision 3 means by weekly-entered. There is one
 * covers store and this is a second way into it, not a second store.
 */
import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  useBasePattern,
  useOccupancyCovers,
  useOverrides,
  useRemoveOverride,
  useSaveOverride,
  useSaveWeeklyCovers,
} from "@/hooks/use-occupancy";
import { useTranslation } from "@/i18n/use-translation";
import { formatDateRange, formatNumber } from "@/lib/format";
import {
  formatDayLabel,
  groupOverridesIntoWeeks,
  splitWeekByPattern,
  type EnteredDay,
  type EnteredWeek,
} from "@/lib/occupancy";
import { weekEndDay, weekStartDay, type DayRange } from "@/lib/zoned-day";

type Props = {
  open: boolean;
  onClose: () => void;
  siteId: string | null;
  siteName: string | null;
  dayRange: DayRange;
  today: string;
};

export function BaseCoversDialog({ open, onClose, siteId, siteName, dayRange, today }: Props) {
  const { locale, t } = useTranslation();
  const anchor = dayRange.from ?? today;
  const [weekDay, setWeekDay] = useState(anchor);
  const [covers, setCovers] = useState("");
  // One week open at a time: the list is a year of rows and several expanded at once turns it
  // into a scroll with no shape. Opening a week closes whichever was open.
  const [openWeek, setOpenWeek] = useState<string | null>(null);

  useEffect(() => {
    if (open) setWeekDay(anchor);
  }, [open, anchor]);

  const weekStart = weekStartDay(weekDay);
  const weekEnd = weekEndDay(weekDay);

  const patternQuery = useBasePattern(siteId, weekStart);
  // The week's current state, so the warning can name how many days already carry a daily edit.
  const weekQuery = useOccupancyCovers(open ? siteId : null, weekStart, weekEnd);
  const saveWeek = useSaveWeeklyCovers();
  const saveOverride = useSaveOverride();
  const removeOverride = useRemoveOverride();
  // Every entered day for the site, grouped back into the weeks this dialog deals in. Not the
  // selected week: the point of the list is to see what has been entered, and that is almost
  // never the week currently in the picker.
  const overridesQuery = useOverrides(open ? siteId : null);
  const weeks = useMemo(
    () => groupOverridesIntoWeeks(overridesQuery.data?.items ?? []),
    [overridesQuery.data],
  );

  const days = useMemo(() => weekQuery.data ?? [], [weekQuery.data]);
  // Only `page` edits are at risk. A `widget` day is a previous split of this same week and
  // replacing it is the ordinary case, not a warning.
  const dailyEdits = days.filter((day) => day.base_source === "override" && !day.weekly_entered);

  if (!open) return null;

  const total = Number(covers || 0);

  /** `backfill` cannot be written back — the API refuses it (decision 10) — and it is
   *  weekly-entered like `widget`, so restoring it as `widget` preserves the only thing
   *  downstream reads from the column. */
  const restorableSource = (source: string) => (source === "page" ? "page" : "widget");

  const restore = (day: EnteredDay) =>
    saveOverride.mutate({
      site_id: siteId as string,
      date: day.date,
      covers: day.covers,
      source: restorableSource(day.source),
    });

  /** Remove a whole week, with undo — it is one figure to the user, so it goes as one. */
  const removeWeek = (week: EnteredWeek) => {
    for (const day of week.days) {
      removeOverride.mutate({ siteId: siteId as string, date: day.date });
    }
    toast(t("dashboard.baseCoversWeekRemoved", { week: week.weekStart }), {
      action: {
        label: t("dashboard.baseCoversUndo"),
        // Each day goes back as the figure *and* the source it actually held, not as an even
        // re-split: a week carrying a hand correction would otherwise lose it on undo, and one
        // restored wholesale as `page` would leave decision 9's weekly-entered block.
        onClick: () => week.days.forEach(restore),
      },
    });
  };

  const split = splitWeekByPattern(total, weekStart, patternQuery.data?.days ?? null);
  // `splitWeekByPattern` falls back to an even split when there is no pattern, or when the one
  // there is sums to zero. The copy has to say which happened: an uneven "130 · 104 · … · 0 · 0"
  // reads as arbitrary until you know it is the site's own shape, and the two zeroes on the end
  // are the pattern saying the site is shut at weekends.
  const shaped = (patternQuery.data?.days ?? []).reduce((sum, day) => sum + day.covers, 0) > 0;

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          {/* One interpolated string, not a muted span: the food spend dialog beside this one
              puts the site in the title's own colour, and two sibling dialogs disagreeing about
              that is the sort of thing you only see once you open both. */}
          <DialogTitle>
            {siteName
              ? t("dashboard.baseCoversTitleForSite", { site: siteName })
              : t("dashboard.baseCoversTitle")}
          </DialogTitle>
          <DialogDescription>{t("dashboard.baseCoversDescription")}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1 text-[13px] font-medium">
              {t("dashboard.baseCoversWeek")}
              {/* Any day snaps to its Mon–Sun week; past weeks are allowed on purpose. */}
              <Input type="date" value={weekDay} onChange={(e) => setWeekDay(e.target.value)} />
              <span className="text-xs font-normal text-muted-foreground">
                {weekStart} – {weekEnd}
              </span>
            </label>
            <label className="flex flex-col gap-1 text-[13px] font-medium">
              {t("dashboard.baseCoversCovers")}
              <Input
                type="number"
                min={0}
                value={covers}
                onChange={(e) => setCovers(e.target.value)}
              />
              {/* Only once there is a figure to split. "0 · 0 · 0 · 0 · 0 · 0 · 0" under an
                  empty input is noise that looks like data. */}
              {covers !== "" && (
                <span className="text-xs font-normal text-muted-foreground">
                  {shaped
                    ? t("dashboard.baseCoversSplitPattern", { split: split.join(" · ") })
                    : t("dashboard.baseCoversSplitEven", { split: split.join(" · ") })}
                  {/* The split shows seven figures, but not that all seven are *written* — a day
                      removed earlier comes back, and only `page` days get the warning above. */}{" "}
                  {t("dashboard.baseCoversWholeWeek")}
                </span>
              )}
            </label>
          </div>

          {dailyEdits.length > 0 && (
            <p className="rounded-md bg-orange-50 px-2.5 py-2 text-xs text-orange-800">
              {t("dashboard.baseCoversWarning", { count: dailyEdits.length })}
            </p>
          )}

          <Button
            className="self-start"
            disabled={covers === "" || !siteId || saveWeek.isPending}
            onClick={() =>
              saveWeek.mutate(
                {
                  siteId: siteId as string,
                  weekStart,
                  covers: total,
                  pattern: patternQuery.data?.days ?? null,
                },
                {
                  onSuccess: () => {
                    setCovers("");
                    toast.success(
                      t("dashboard.baseCoversSaved", { total: formatNumber(locale, total) }),
                    );
                  },
                },
              )
            }
          >
            {t("dashboard.baseCoversSave")}
          </Button>

          {/* Structurally the food spend dialog's weeks list — same heading, same row, same
              controls. They are two cards in one row on the homepage opening two dialogs that do
              the same job, so they read as one thing or they read as an oversight. */}
          <div className="mt-2 flex flex-col gap-1">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
              {t("dashboard.baseCoversEntered")}
            </p>
            {weeks.length === 0 ? (
              <p className="py-4 text-center text-sm text-gray-500">
                {t("dashboard.baseCoversNone")}
              </p>
            ) : (
              // Capped height: a year of weekly entries is ~52 rows, and a backfilled site more
              // still. The dialog keeps its shape either way.
              <ul className="flex max-h-64 flex-col overflow-y-auto">
                {weeks.map((week) => (
                  <li key={week.weekStart} className="flex flex-col">
                    <div className="flex items-center gap-1 rounded-md px-2 py-1.5 hover:bg-black/[0.02]">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        className="-ml-1.5 shrink-0"
                        aria-label={
                          openWeek === week.weekStart
                            ? t("dashboard.baseCoversCollapseWeek")
                            : t("dashboard.baseCoversExpandWeek")
                        }
                        aria-expanded={openWeek === week.weekStart}
                        onClick={() =>
                          setOpenWeek(openWeek === week.weekStart ? null : week.weekStart)
                        }
                      >
                        {openWeek === week.weekStart ? (
                          <ChevronDown className="size-4 text-gray-400" />
                        ) : (
                          <ChevronRight className="size-4 text-gray-400" />
                        )}
                      </Button>

                      <div className="flex min-w-0 flex-col">
                        <span className="text-sm font-medium tabular-nums text-gray-900">
                          {formatNumber(locale, week.covers)}
                        </span>
                        <span className="truncate text-xs text-gray-400">
                          {formatDateRange(locale, week.weekStart, week.weekEnd)}
                          {/* A part-entered week is not the same claim as a full one, and saying
                              which it is costs one separator. */}
                          {week.daysEntered < 7 &&
                            ` · ${t("dashboard.baseCoversPartWeek", { days: week.daysEntered })}`}
                        </span>
                      </div>

                      {/* A week somebody has since corrected on the Occupancy page is no longer the
                          figure they typed here, and re-saving would overwrite that correction. */}
                      {week.hasDailyEdit && (
                        <span className="ml-2 shrink-0 rounded border border-black/[0.08] px-1 text-[10px] font-medium uppercase tracking-wide text-gray-400">
                          {t("dashboard.baseCoversEditedTag")}
                        </span>
                      )}

                      <div className="ml-auto flex shrink-0 items-center gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label={t("dashboard.baseCoversEditWeek")}
                          // Loads the week back into the picker and its total into the input, so
                          // re-saving re-splits it — the same path a fresh entry takes.
                          onClick={() => {
                            setWeekDay(week.weekStart);
                            setCovers(String(week.covers));
                          }}
                        >
                          <Pencil className="size-4 text-gray-400" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label={t("dashboard.baseCoversRemoveWeek")}
                          onClick={() => removeWeek(week)}
                        >
                          <Trash2 className="size-4 text-gray-400" />
                        </Button>
                      </div>
                    </div>

                    {openWeek === week.weekStart && (
                      <ul className="mb-1 ml-8 mr-2 overflow-hidden rounded-md bg-black/[0.02]">
                        {week.days.map((day) => (
                          <li
                            key={day.date}
                            className="flex items-center gap-2 px-2.5 py-1 text-xs"
                          >
                            <span className="text-gray-400">
                              {formatDayLabel(locale, day.date)}
                            </span>
                            {/* Which days are `page` is the whole explanation for the week's
                                Edited tag, and for why re-saving the week would replace them.
                                Plain text, not a second badge: the list is dense. */}
                            {day.source === "page" && (
                              <span className="text-[10px] uppercase tracking-wide text-gray-400">
                                {t("dashboard.baseCoversEditedTag")}
                              </span>
                            )}
                            <span className="ml-auto tabular-nums text-gray-700">
                              {formatNumber(locale, day.covers)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {overridesQuery.data?.has_more && (
              <p className="text-xs text-muted-foreground">{t("dashboard.baseCoversMoreWeeks")}</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
